import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { captureException } from '@sentry/react';

import type { PatientDuplicateSummary, MatchedPipeline } from '@shared/patient-intake-status';

import { db } from '../firebase-config';

export type DuplicateFlag = {
  id: string;
  patientIds: [string, string];
  patients: Record<string, PatientDuplicateSummary>;
  matchedPipelines: MatchedPipeline[];
  highestTier: number;
  resolvedAt?: unknown;
  resolution?: string;
};

type UseDuplicateFlagsState = {
  data: DuplicateFlag[];
  isLoading: boolean;
  error: Error | null;
};

export const useDuplicateFlags = (patientId: string) => {
  const [state, setState] = useState<UseDuplicateFlagsState>({
    data: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const q = query(collection(db, 'duplicateFlags'), where('patientIds', 'array-contains', patientId));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const flags = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as DuplicateFlag[];

        setState({ data: flags, isLoading: false, error: null });
      },
      (error) => {
        captureException(error, {
          tags: { issueIn: 'useDuplicateFlags' },
          extra: { patientId },
        });
        setState({ data: [], isLoading: false, error });
      },
    );

    return () => unsubscribe();
  }, [patientId]);

  return state;
};
